// Test connection: post, then wait for the row to say what the far side said (#603).
//
// The route answers at once and the outcome lands on the connection's `last_check`
// afterwards (`routes/integrations.py`, `run_test`), so showing it is a wait on this side:
// read that one connection until its check is one the row did not hold when the test was
// posted. The words are `connectionHealth.ts`'s: a test's answer is a check's answer.

import { messageOf, type Api } from "../api/http";
import { enableIntegration, readIntegration, sendTestDelivery, testIntegration, type IntegrationRow } from "../api/integrations";
import { channelWentUnread, failedCheckInWords, testDeliveryInWords } from "./connectionHealth";

/**
 * How long the console goes on reading before it says the test has not answered. The
 * server gives one check the bound of one send (`delivery.SEND_TIMEOUT_SECONDS`) and
 * then writes a timed-out answer, so this only has to outlast that; past it, the answer
 * is still written and the next load of the page shows it.
 */
const READS = 15;
const BETWEEN_READS_MS = 1000;

const sleepFor = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A read that failed while an answer was being waited for (#615). What was asked — a test,
 * or the check a save starts — was asked; it is the answer that could not be read, and the
 * row still gets it. `cause` is the read's own failure.
 */
export class AnswerUnread extends Error {
  readonly cause: unknown;

  constructor(cause: unknown) {
    super(`the answer could not be read: ${messageOf(cause)}`);
    this.name = "AnswerUnread";
    this.cause = cause;
  }
}

/** Where an answer that has not arrived, or could not be read, will be found. */
const LOOK_UNDER_HEALTH = "Reload this page in a moment; the answer appears under Health.";

/**
 * Test one connection. The connection carrying the test's answer, or `null` when none
 * arrived inside the reads allowed.
 *
 * A check is the test's own when it is **there and not the one the row held before**.
 * "Different" alone is not enough: an edit clears `last_check`, and a cleared check is
 * no outcome.
 */
export async function testConnection(
  api: Api,
  id: string,
  waiting: { reads?: number; sleep?: (ms: number) => Promise<void> } = {},
): Promise<IntegrationRow | null> {
  const before = (await testIntegration(api, id)).last_check?.checked_at ?? null;
  return newCheckAfter(api, id, before, { ...waiting, wanted: "check" });
}

/**
 * Reconnect one disconnected connection (#820): post, then wait for the check that decides
 * whether it goes live. The connection carrying that check's answer — live only if it
 * passed — or `null` when none arrived. A connection that was already live is answered as
 * it is, and nothing is waited for.
 */
export async function enableConnection(
  api: Api,
  id: string,
  waiting: { reads?: number; sleep?: (ms: number) => Promise<void> } = {},
): Promise<IntegrationRow | null> {
  const posted = await enableIntegration(api, id);
  if (posted.active) return posted;
  return newCheckAfter(api, id, posted.last_check?.checked_at ?? null, { ...waiting, wanted: "check" });
}

/**
 * Send a webhook receiver its test event (#1076): post, then wait for the receiver's answer,
 * which lands on the row's `last_check` as a test delivery's does. The connection carrying it,
 * or `null` when none arrived inside the reads allowed.
 */
export async function sendTestEvent(
  api: Api,
  id: string,
  waiting: { reads?: number; sleep?: (ms: number) => Promise<void> } = {},
): Promise<IntegrationRow | null> {
  const before = (await sendTestDelivery(api, id)).last_check?.checked_at ?? null;
  return newCheckAfter(api, id, before, { ...waiting, wanted: "test_delivery" });
}

/**
 * Read one connection until it carries an answer of the `wanted` kind that is not the
 * one stamped `before`, or the reads run out. The wait a test makes, and the one a test
 * delivery's answer is waited for with (#614).
 *
 * The kind matters because two can be out together: the Jira flow sends its test work
 * item straight after the save, whose own check is still being asked, and either may
 * answer first. An answer of the other kind is somebody else's.
 */
export async function newCheckAfter(
  api: Api,
  id: string,
  before: string | null,
  {
    reads = READS,
    sleep = sleepFor,
    wanted,
  }: { reads?: number; sleep?: (ms: number) => Promise<void>; wanted: "check" | "test_delivery" | "any" },
): Promise<IntegrationRow | null> {
  for (let read = 0; read < reads; read += 1) {
    await sleep(BETWEEN_READS_MS);
    let row: IntegrationRow;
    try {
      row = await readIntegration(api, id);
    } catch (failed) {
      throw new AnswerUnread(failed);
    }
    const check = row.last_check ?? null;
    if (check !== null && check.checked_at !== before && (wanted === "any" || (check.what ?? "check") === wanted)) return row;
  }
  return null;
}

/**
 * Wait for the answer a save's check leads to (#615): `newCheckAfter`, with nothing posted first.
 *
 * `before` is null because a save clears `last_check` in the same write
 * (`integrations/setup.py`, `update`) and a new connection has none. **Any kind of answer is
 * taken**: a flow sends its test work item or message straight after the save, and a check
 * that began before that send answered never writes over it (`integrations/check.py`,
 * `asked_and_kept`), so on the default Slack path the test message's answer may be the only
 * one the row ever carries. Either is an answer about the connection as saved.
 */
export function savedCheck(
  api: Api,
  id: string,
  waiting: { reads?: number; sleep?: (ms: number) => Promise<void> } = {},
): Promise<IntegrationRow | null> {
  return newCheckAfter(api, id, null, { ...waiting, wanted: "any" });
}

/** What the row says when a save's check did not answer inside the wait. */
export const CHECK_NOT_ANSWERED = `The check has not answered yet. ${LOOK_UNDER_HEALTH}`;

/** What a failed read mid-wait means, for a test or for a save's check: asked, and unread. */
export function unreadInWords(asked: "test" | "save" | "enable", unread: AnswerUnread): string {
  const lead =
    asked === "test"
      ? "The test was asked for, and its answer"
      : asked === "enable"
        ? "The connection is being checked before it reconnects, and the answer"
        : "The connection was saved and is being checked, and the answer";
  return `${lead} could not be read: ${messageOf(unread.cause)} ${LOOK_UNDER_HEALTH}`;
}

/** What a reconnect came to, under the row's buttons (#820): live, the check's refusal, or not yet. */
export function enabledInWords(enabled: IntegrationRow | null): string {
  const check = enabled?.last_check ?? null;
  if (enabled?.active === true) return "Reconnected. The connection answered.";
  if (enabled === null || check === null || check.status === "passed") return CHECK_NOT_ANSWERED;
  return `Not reconnected. ${failedCheckInWords(enabled.kind, check)}`;
}

/** What a receiver said of its test event, under the row's buttons (#1076), or that it has not yet. */
export function sentTestInWords(sent: IntegrationRow | null): string {
  const check = sent?.last_check ?? null;
  if (sent === null || check === null) return `The test event has not answered yet. ${LOOK_UNDER_HEALTH}`;
  const said = testDeliveryInWords(sent.kind, check);
  return check.status === "passed" ? said : `The test event was not accepted. ${said}`;
}

/** What a test just said, under the row's buttons — or that it has not said anything yet. */
export function testedInWords(tested: IntegrationRow | null): string {
  const check = tested?.last_check ?? null;
  if (tested === null || check === null) {
    return `The test has not answered yet. ${LOOK_UNDER_HEALTH}`;
  }
  if (check.status === "passed") {
    // Another missing scope than `channels:read` is shown as Slack said it (#605).
    if (!channelWentUnread(check) && /\bmissing_scope\b/.test(check.last_detail)) return `Tested just now. ${check.last_detail}`;
    return channelWentUnread(check)
      ? "Tested just now. Slack accepted the token. The channel could not be checked, because the app was installed without channels:read."
      : "Tested just now. The connection answered.";
  }
  return `Tested just now. ${failedCheckInWords(tested.kind, check)}`;
}
